import { useContext, useEffect, useRef, useState } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import styles from "../../../../components/TemplateSelectionScreen/TemplateSelectionScreen.module.css";
import { Card, Checkbox, Icon, RadioButton, Tooltip } from "@shopify/polaris";
import { ArrowLeftIcon, InfoIcon } from "@shopify/polaris-icons";
import { Link } from "react-router-dom";
import VolumeTemplateLivePreview from "./VolumeTemplateLivePreview";
import VolumeThemeColorPicker from "./VolumeThemeColorPicker";
import { OfferInterface, VolumeBundleDesignSummary, VolumeConfigInterface } from "../../Types";
import { AppConfigContext } from "../../../../context/AppConfigContext";
import { endPoints, getData } from "../../../../utilities/networkCalls";
import { BundleTypes } from "../../constants";
import { scrollToTop } from "../../../../utilities/functions";

interface VolumeTemplateSelectionScreenProps {
    makeClean: () => void;
}

const VolumeTemplateSelectionScreen = ({ makeClean }: VolumeTemplateSelectionScreenProps) => {
    const { control, setValue, getValues } = useFormContext();
    const selectedTemplateVolume = useWatch({ control, name: "selected_template_volume" });
    const { globalSettingsInfo } = useContext(AppConfigContext);

    const [selectedLayout, setSelectedLayout] = useState<"vertical" | "horizontal">(
        selectedTemplateVolume?.startsWith("horizontal") ? "horizontal" : "vertical",
    );

    const [previousVolumeBundles, setPreviousVolumeBundles] = useState<VolumeBundleDesignSummary[]>([]);
    const [loadingPreviousVolumeBundles, setLoadingPreviousVolumeBundles] = useState(true);
    const [useDesignFromPreviousBundle, setUseDesignFromPreviousBundle] = useState(false);
    const ownDesignRef = useRef<VolumeConfigInterface["bundles_design"] | null>(null);

    useEffect(() => {
        const fetchPreviousVolumeBundles = async () => {
            const res = await getData({
                url:
                    endPoints.getBundleDesignsByType +
                    `?bundle_type=${BundleTypes.VOLUME_DISCOUNT}&sort_by=updated_at&order=desc`,
            });
            if (res.status === 200 && res?.data?.bundles?.length) {
                setPreviousVolumeBundles(res.data.bundles);
            }
            setLoadingPreviousVolumeBundles(false);
        };
        fetchPreviousVolumeBundles();
    }, []);

    const handleUseDesignFromPreviousBundle = (checked: boolean) => {
        setUseDesignFromPreviousBundle(checked);

        if (!checked) {
            if (ownDesignRef.current) {
                setValue("bundles_design", ownDesignRef.current, { shouldDirty: true });
            }
            return;
        }

        ownDesignRef.current = getValues("bundles_design");

        // previousVolumeBundles already comes sorted most-recently-updated-first
        // from the get-volume-bundle-designs endpoint.
        const bundleWithDesign = previousVolumeBundles.find((bundle) => bundle?.bundles_design);

        const designToApply =
            bundleWithDesign?.bundles_design || globalSettingsInfo?.globalSettings?.design_settings_volume;

        if (designToApply) {
            setValue("bundles_design", { ...getValues("bundles_design"), ...designToApply }, { shouldDirty: true });
        }
    };

    // `overrides` mirrors the card's preview configOverrides. `products` is dropped
    // because it only exists to show dummy products in the preview thumbnail and must
    // never overwrite the merchant's real product selection. `volume_discounts` is
    // applied field-by-field per offer, but the product-list fields on each offer
    // (bundle_products, upsell_products, free_gifts) are dropped for the same reason —
    // everything else on the offer (discount settings, offer type, toggles, copy) is
    // template data and should be applied.
    const handleChangeSelectedTemplate = (template: string, overrides: Partial<VolumeConfigInterface> = {}) => {
        setValue("selected_template_volume", template, { shouldDirty: true });
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { products: _products, volume_discounts, ...applicableOverrides } = overrides;
        Object.entries(applicableOverrides).forEach(([key, value]) => {
            setValue(key, value, { shouldDirty: true });
        });

        if (volume_discounts?.length) {
            const currentOffers = (getValues("volume_discounts") as OfferInterface[]) || [];
            const mergedOffers = volume_discounts.map((offerOverride, index) => {
                // eslint-disable-next-line @typescript-eslint/no-unused-vars
                const { bundle_products, upsell_products, free_gifts, ...applicableOfferFields } = offerOverride;
                return {
                    ...currentOffers[index],
                    ...applicableOfferFields,
                };
            });
            setValue("volume_discounts", mergedOffers, { shouldDirty: true });
        }

        makeClean();
        scrollToTop();
    };

    return (
        <div className={styles.templateSelectionWrapper}>
            <div
                style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    flexWrap: "wrap",
                    gap: "12px",
                }}
            >
                <h1 className={styles.templateSelectionHeading} style={{ margin: 0, alignItems: "center" }}>
                    <Link to={"/"} style={{ display: "flex", alignItems: "center" }}>
                        <ArrowLeftIcon width={18} className={styles.templateSelectionBackButton} />
                    </Link>
                    Choose a template
                </h1>
                <VolumeThemeColorPicker disabled={useDesignFromPreviousBundle} />
            </div>
            <div className={styles.templateSelectionSubHeading} style={{ marginBottom: 0, fontWeight: 400 }}>
                Pick a template to start with — everything can be customized inside to match your brand.
            </div>

            <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                <Checkbox
                    label="Use design settings from my most recently updated volume bundle"
                    checked={useDesignFromPreviousBundle}
                    disabled={loadingPreviousVolumeBundles || previousVolumeBundles.length === 0}
                    onChange={handleUseDesignFromPreviousBundle}
                />
                {!loadingPreviousVolumeBundles && previousVolumeBundles.length === 0 && (
                    <Tooltip content="You don't have any previous quantity breaks bundles yet — create and save one first to reuse its design here.">
                        <Icon source={InfoIcon} tone="subdued" />
                    </Tooltip>
                )}
            </div>

            <div
                style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "4px",
                }}
            >
                <RadioButton
                    label="Vertical Templates"
                    id="volume_layout_vertical"
                    checked={selectedLayout === "vertical"}
                    onChange={() => setSelectedLayout("vertical")}
                />
                <RadioButton
                    label="Horizontal Templates"
                    id="volume_layout_horizontal"
                    checked={selectedLayout === "horizontal"}
                    onChange={() => setSelectedLayout("horizontal")}
                />
            </div>

            <Card>
                <div className={styles.templateSelectionSubHeading}>Select template from below</div>
                <VolumeTemplateLivePreview onSelectTemplate={handleChangeSelectedTemplate} layout={selectedLayout} />
            </Card>
        </div>
    );
};

export default VolumeTemplateSelectionScreen;
